import * as React from "react";
import { useHistory } from "react-router-dom";
import { CellContext } from "@tanstack/react-table";
import styled from "styled-components";
import { useMappedState } from "redux-react-hook";

import Table from "src/core/dashboard/components/widgets/table/Table";
import { Button } from "src/library/components/Button";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { IOrder } from "src/core/orders/api/orders.api";
import { adminOrderTableNewColumns } from "src/core/shared/components/tables/adminOrderTableColumns";
import { getOrderTableColumns } from "src/core/shared/components/tables/ordersTableColumns";
import type { IStore } from "src/core/shared/store";

import { useAccountingApi } from "../api/accounting.api";

interface RelatedOrdersTabProps {
  /** Invoice ID to fetch orders for */
  invoiceId: number;
}

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "orderDeliveryDate", "DESC" ] ]
};

/**
 * Related Orders Tab - displays orders in a table format
 * Used in: InvoiceDetailsPage
 */
const RelatedOrdersTab: React.FC<RelatedOrdersTabProps> = ({ invoiceId }) => {
  const history = useHistory();

  // Get current role, terminology, and other settings needed for table columns
  const {
    currentRole,
    terminology,
    orderStatuses,
    installedModules
  } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    terminology: global.settings?.terminology,
    orderStatuses: global.settings?.orderStatuses || [],
    installedModules: global.settings?.installedModules || []
  }));

  const isAdmin = currentRole?.type === "admin";
  const [ orders, setOrders ] = React.useState<IOrder[]>([]);
  const [ pageCount, setPageCount ] = React.useState(1);
  const [ rowCount, setRowCount ] = React.useState(0);
  // API hook for fetching invoice orders
  const [ ordersResponse, fetchOrders ] = useAccountingApi("LIST_INVOICE_ORDERS");

  // Action button for each row
  const renderActionButton = React.useCallback((info: CellContext<IOrder, unknown>) => {
    const order = info.row.original;

    return (
      <Button
        $buttonType="primary"
        onClick={() => history.push(`/orders/${order.id}`)}
      >
        View Order
      </Button>
    );
  },
  [ history ],
  );

  // Build columns based on role - reuse existing table column definitions
  // Disable filtering since this is static data
  const columns = React.useMemo(() => {
    if (!terminology) {
      return [];
    }

    let baseColumns;

    // Use admin columns for admin users (includes Customer and Drop columns)
    if (isAdmin) {
      baseColumns = adminOrderTableNewColumns({
        actionButton: renderActionButton,
        orderStatuses,
        terminology,
        installedModules,
        pricesVisible: false,
        selectedColumns: [
          "orderReference",
          "customer",
          "branch",
          "customerReference",
          "type",
          "createdAt",
          "orderDeliveryDate",
          "orderStatus",
          "actions"
        ]
      });
    } else {
      // Use customer columns for customer users (no Customer/Drop columns)
      baseColumns = getOrderTableColumns({
        actionButton: renderActionButton,
        orderStatuses,
        terminology,
        installedModules,
        pricesVisible: false,
        selectedColumns: [
          "orderReference",
          "customerReference",
          "type",
          "createdAt",
          "orderDeliveryDate",
          "orderStatus",
          "actions"
        ]
      });
    }

    return baseColumns;
  }, [
    isAdmin,
    terminology,
    orderStatuses,
    installedModules,
    renderActionButton
  ]);

  // Fetch data when filters or pagination change
  const onFetchData = React.useCallback(
    (params: { filters: FilterParams<IOrder>; pagination: PaginatedParams<IOrder> }) => {
      fetchOrders({
        invoiceId,
        pagination: params.pagination,
        filters: {
          ...params.filters,
          invoiceId
        }
      });
    },
    [ fetchOrders, invoiceId ]
  );

  // Update state when API response changes
  React.useEffect(() => {
    if (ordersResponse.data?.orders) {
      setOrders(ordersResponse.data.orders.result);
      setPageCount(ordersResponse.data.orders.pages);
      setRowCount(ordersResponse.data.orders.count);
    }
  }, [ ordersResponse.data ]);

  return (
    <Table
      tableOf="Related Orders"
      data={orders}
      columns={columns}
      loading={ordersResponse.pending}
      error={ordersResponse.error}
      onFetchData={onFetchData}
      defaultPagination={defaultPagination}
      pageCount={pageCount}
      rowCount={rowCount}
    />
  );
};

export default RelatedOrdersTab;
